import { useEffect, useRef, useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch } from "react-redux";
import { getProfileAction } from "../store/Profile/ProfileAction";
import { setUserAccessTokenAction, updateUserProfileInfoAction } from "../store/Users/UsersAction";
import { pickPreferredCmsProfile } from "../utils/portalAccess";
import {
  clearAccessTokenGetter,
  getAuthAudience,
  setAccessTokenGetter,
  setStoredAccessToken,
} from "../utils/authTokenService";

const AuthTokenProvider = ({ children }) => {
  const { isAuthenticated, isLoading, getAccessTokenSilently, user } = useAuth0();
  const dispatch = useDispatch();

  const [ready, setReady] = useState(false);
  const fetchedProfileKeyRef = useRef(null);
  const inFlightProfileKeyRef = useRef(null);

  useEffect(() => {
    setAccessTokenGetter(async ({ forceRefresh = false } = {}) => {
      const token = await getAccessTokenSilently({
        cacheMode: forceRefresh ? "off" : "on",
        authorizationParams: {
          audience: getAuthAudience(),
        },
      });
      setStoredAccessToken(token);
      dispatch(setUserAccessTokenAction(token));
      return token;
    });

    return () => {
      clearAccessTokenGetter();
    };
  }, [dispatch, getAccessTokenSilently]);

  useEffect(() => {
    const finish = () => setReady(true);

    const initAuth = async () => {
      if (isLoading) return;

      if (!isAuthenticated) {
        fetchedProfileKeyRef.current = null;
        inFlightProfileKeyRef.current = null;
        localStorage.removeItem("token");
        dispatch(setUserAccessTokenAction(null));
        dispatch(updateUserProfileInfoAction({}));
        finish();
        return;
      }

      const profileKey = user?.sub || "authenticated-user";
      const pathName = window.location.pathname;
      const routeOwnsValidation = pathName === "/validate-user" || pathName === "/login";

      if (routeOwnsValidation) {
        // `/validate-user` is the source of truth for first-login routing. Avoid
        // running a second /profile bootstrap here, which can race the validator.
        finish();
        return;
      }

      if (fetchedProfileKeyRef.current === profileKey) {
        finish();
        return;
      }

      if (inFlightProfileKeyRef.current === profileKey) {
        return;
      }

      setReady(false);

      try {
        // Always resolve the token from the active Auth0 session. A stored token can
        // belong to a previous login and would make /profile return the wrong role.
        const token = await getAccessTokenSilently({
          authorizationParams: {
            audience: process.env.REACT_APP_AUDIENCE_URL,
          },
        });
        dispatch(setUserAccessTokenAction(token));
        localStorage.setItem("token", token);

        inFlightProfileKeyRef.current = profileKey;
        dispatch(
          getProfileAction({
            apiPayloadRequest: {},
            callback: (res) => {
              inFlightProfileKeyRef.current = null;

              if (res?.type === 1 && Array.isArray(res?.data) && res.data.length > 0) {
                const profileData = pickPreferredCmsProfile(res.data) || res.data[0];
                dispatch(updateUserProfileInfoAction(profileData || {}));
                fetchedProfileKeyRef.current = profileKey;
              } else {
                dispatch(updateUserProfileInfoAction({}));
              }

              finish();
            },
          })
        );
      } catch (err) {
        inFlightProfileKeyRef.current = null;
        localStorage.removeItem("token");
        dispatch(setUserAccessTokenAction(null));
        dispatch(updateUserProfileInfoAction({}));
        finish();
      }
    };

    initAuth();
  }, [dispatch, getAccessTokenSilently, isAuthenticated, isLoading, user?.sub]);

  if (!ready) {
    return (
      <div className="flex items-center justify-center h-screen text-xl font-bold">
        Loading authentication...
      </div>
    );
  }

  return children;
};

export default AuthTokenProvider;
